import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import Reveal from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import { T, useContent } from "@/content/ContentContext";

const IMG = "https://images.unsplash.com/photo-1781513144825-aa1e284c5950?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMGx1eHVyeXxlbnwwfHx8fDE3ODc3MzY1Mzh8MA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { n: "01", k: "consulting.pillar.1" },
  { n: "02", k: "consulting.pillar.2" },
  { n: "03", k: "consulting.pillar.3" },
];

export default function ConsultingPage() {
  const { t, list } = useContent();
  return (
    <div data-testid="consulting-page">
      <PageHero
        overline={t("consulting.hero.overline")}
        lines={[<T key="1" k="consulting.hero.line1" />, <T key="2" k="consulting.hero.line2" em="text-brand-gold" />]}
        intro={t("consulting.hero.intro")}
        image={IMG}
        imageAlt="Modern clinic reception"
      />

      <section className="bg-brand-bone py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-brand-ink mt-4" data-testid="pillars-heading">{t("consulting.pillars.heading")}</h2>
          </Reveal>
          <div className="mt-14 space-y-px bg-brand-ink/10 border border-brand-ink/10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06} className="bg-brand-bone">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12" data-testid={`pillar-${p.n}`}>
                  <span className="lg:col-span-1 font-sans text-sm font-bold text-brand-gold tracking-[0.2em] pt-1">{p.n}</span>
                  <div className="lg:col-span-6">
                    <h3 className="font-serif text-2xl lg:text-3xl font-medium text-brand-ink">{t(`${p.k}.title`)}</h3>
                    <p className="mt-4 text-sm lg:text-base font-light leading-relaxed text-brand-ink/65">{t(`${p.k}.body`)}</p>
                  </div>
                  <ul className="lg:col-span-5 space-y-3 lg:border-l lg:border-brand-ink/10 lg:pl-10">
                    {list(`${p.k}.items`).map((item) => (
                      <li key={item} className="flex items-center gap-3 text-sm text-brand-ink/75">
                        <span className="h-1.5 w-1.5 rotate-45 bg-brand-gold shrink-0" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep text-brand-bone py-24 lg:py-32 texture-grain">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mt-4 leading-[1.1]" data-testid="consulting-difference-heading">
                <T k="consulting.difference.heading" em="text-brand-gold" />
              </h2>
              <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-brand-bone/65">
                {t("consulting.difference.body")}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Reveal delay={0.1}>
              <Link to="/contact" data-testid="consulting-cta" className="inline-flex items-center gap-3 rounded-full bg-brand-gold px-8 py-4 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight hover:-translate-y-0.5">
                {t("consulting.cta")} <ArrowRight size={16} weight="bold" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
